import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { parse } from 'querystring';
import Alert from '@mui/material/Alert';
import { useEffect, useMemo, useRef } from 'react';
import { orderConfirmation } from '../../../../layout/routes';
import {
  Feature,
  OrderProductPreview,
  PlanInterval,
  getFeatureByAddOn,
} from '../../../../models';
import { fromOrder, fromPromotion, fromSubscription } from '../../../../store';
import { QueryDictionary, useNavigate, useQuery } from '../../../../routes';
import { formatCurrency } from '../../../../components';
import { useGetTrackingCookies, usePlanCartSnapshot } from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { sendTrackingData, TrackingPayload } from '../../../../utils/utils';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';
import {
  buildPreviewProducts,
  getCloudSessionQuota,
  getFinalTrueBundleSeatQuota,
  getPaidAddOnLineItems,
  getPreviewProductFeature,
  getSelectedTrueBundleSeatQuota,
  getTrueBundlePreviewRequests,
  getTrueBundleTitleId,
  hasSelectedPreviewRequest,
  hasPreviewResponseForRequests,
  isMonthlyToAnnualSwitchPreview,
} from './trueBundlePreview';
import { getStarterPackageSummaryLine } from './starterPackageCampaign';
import {
  OrderSummaryLineItems,
  getSummaryQuantityLabel,
  getSummaryUnitWord,
} from './OrderSummaryLineItems';
import { OrderSummaryCoupon, formatCouponDiscount } from './OrderSummaryCoupon';
import { splitDiscountRows } from './orderSummaryDiscount';

const useStyles = makeStyles(theme => ({
  orderSummaryCard: {
    border: '1px solid #ececf4',
    borderRadius: 16,
    padding: theme.spacing(2.75),
    backgroundColor: '#ffffff',
    height: 'fit-content',
    position: 'sticky',
    top: theme.spacing(2),
  },
  orderSummaryTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 18,
    fontWeight: 600,
    color: '#16141d',
  },
  productRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.4, 0),
  },
  productBlock: {
    borderBottom: '1px solid #F0EFF6',
  },
  productName: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.35,
    color: '#22283c',
  },
  productQuantity: {
    fontSize: 13,
    lineHeight: 1.45,
    color: '#9CA3AF',
    marginTop: theme.spacing(0.25),
  },
  productQuantityWithInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  productQuantityInfoIcon: {
    color: '#9CA3AF',
    cursor: 'help',
    fontSize: 16,
  },
  productAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#22283c',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productOriginalAmount: {
    fontSize: 12.5,
    color: '#9CA3AF',
    textDecoration: 'line-through',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productTaxAmount: {
    fontSize: 12.5,
    color: '#808B9A',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productFreeAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#16865F',
    textAlign: 'right',
  },
  includedFreeLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '.08em',
    color: '#808B9A',
    textTransform: 'uppercase',
    paddingTop: theme.spacing(1.75),
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.75, 0),
  },
  summaryLabel: {
    fontWeight: 400,
    fontSize: 14,
    color: '#808B9A',
  },
  summaryValue: {
    fontWeight: 600,
    fontSize: 14,
    color: '#22283c',
  },
  summaryValueHighlight: {
    fontWeight: 700,
    fontSize: 14,
    color: '#5959EB',
  },
  discountValue: {
    fontWeight: 800,
    fontSize: 14,
    color: '#16865F',
  },
  promotionNote: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: 8,
    color: '#047857',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.45,
    margin: theme.spacing(0.25, 0, 0.75),
    padding: theme.spacing(1, 1.25),
  },
  promotionNoteContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  promotionNoteInfoIcon: {
    color: '#047857',
    cursor: 'help',
    fontSize: 16,
    flexShrink: 0,
  },
  lightTooltip: {
    maxWidth: 360,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#ffffff',
    color: '#374151',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.45,
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  },
  lightTooltipArrow: {
    color: '#ffffff',
  },
  discountLabelGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  discountInfoIcon: {
    color: '#12a074',
    cursor: 'help',
    fontSize: 16,
  },
  darkTooltip: {
    maxWidth: 340,
    padding: theme.spacing(1.5, 1.75),
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    border: '1px solid rgba(255, 255, 255, .14)',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  darkTooltipTitle: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 700,
    marginBottom: theme.spacing(0.875),
  },
  discountDetailRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.75),
    padding: theme.spacing(0.375, 0),
  },
  discountDetailLabel: {
    color: '#c8d8d1',
    fontSize: 12,
  },
  discountDetailAmount: {
    whiteSpace: 'nowrap',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginTop: theme.spacing(0.375),
    paddingTop: theme.spacing(1.5),
    borderTop: '2px solid #ececf4',
  },
  totalLabel: {
    fontWeight: 700,
    fontSize: 15,
    color: '#16141d',
  },
  totalAmount: {
    fontWeight: 700,
    fontSize: 22,
    color: '#16141d',
    textAlign: 'right',
  },
  totalAmountLoading: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 96,
    height: 30,
    color: '#16141d',
  },
  // Group mode shows the spinner on its own while the whole totals block loads —
  // center it in the card instead of the right-aligned amount slot.
  previewLoadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2.5, 0),
    color: '#9a98ad',
  },
  billedLabel: {
    fontWeight: 400,
    fontSize: 12,
    color: '#808B9A',
    textAlign: 'right',
    marginBottom: theme.spacing(1),
  },
  intervalSectionLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  intervalTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  intervalTotalLabel: {
    fontWeight: 500,
    fontSize: 13.5,
    color: '#73718a',
  },
  intervalTotalAmount: {
    fontWeight: 600,
    fontSize: 14,
    color: '#16141d',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  recurringNote: {
    fontSize: 12,
    lineHeight: 1.45,
    color: '#9a98ad',
    marginTop: theme.spacing(1),
  },
  checkoutButton: {
    backgroundColor: '#0f8461',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 15,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#b0b0b0',
      color: '#ffffff',
    },
  },
  checkoutButtonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}));

export const OrderSummary = (
  { isAnnual, groupByInterval = false, showCoupon = false }: {
    isAnnual: boolean;
    groupByInterval?: boolean;
    showCoupon?: boolean;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const { get, queryString } = useQuery();
  const accountId = Number(get('accountId'));
  const { navigate, replaceQuery } = useNavigate();
  const { flags } = useLaunchDarkly();
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const appliedCoupon = useSelector(fromOrder.selectAppliedCoupon);
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const previewCartRef = useRef<string | undefined>(undefined);
  const hasPendingOrder = pendingOrder && pendingOrder.length > 0;
  const orderPreviewRequest = useSelector(fromOrder.selectOrderPreviewRequest);
  const fallbackPreviewRequest = useSelector(fromOrder.selectTruePlatformPreviewRequest);
  const selectedAddOns = useSelector(fromOrder.selectAddOns);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const currentSubscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);
  const fallbackPreviewRequestWithAddOns = useMemo(() => {
    if (!fallbackPreviewRequest) {
      return undefined;
    }
    const paidAddOns = Object.fromEntries(
      Object.entries(selectedAddOns ?? {}).filter(([, quantity]) => quantity > 0),
    );
    return {
      ...fallbackPreviewRequest,
      ...(Object.keys(paidAddOns).length > 0 ? { addOns: paidAddOns } : {}),
    };
  }, [fallbackPreviewRequest, selectedAddOns]);
  const previewRequests = getTrueBundlePreviewRequests(
    orderPreviewRequest,
    fallbackPreviewRequestWithAddOns,
  );
  const hasFreshPreviewResponse = hasPreviewResponseForRequests(
    previewRequests,
    orderPreviewResponse,
  );
  const hasSelectedPreviewSelection = previewRequests.some(
    request => hasSelectedPreviewRequest(request, currentSubscriptions),
  );
  const nextProductsPreview = useMemo(
    () => (hasFreshPreviewResponse
      ? buildPreviewProducts(
        previewRequests,
        orderPreviewResponse,
        flags?.subscriptionPlanPrice,
      )
      : []),
    [
      flags?.subscriptionPlanPrice,
      hasFreshPreviewResponse,
      orderPreviewResponse,
      previewRequests,
    ],
  );
  const lastStableProductsPreviewRef = useRef<OrderProductPreview[]>([]);
  useEffect(() => {
    if (!hasSelectedPreviewSelection) {
      lastStableProductsPreviewRef.current = [];
      return;
    }

    if (hasFreshPreviewResponse) {
      lastStableProductsPreviewRef.current = nextProductsPreview;
    }
  }, [hasFreshPreviewResponse, hasSelectedPreviewSelection, nextProductsPreview]);
  const productsPreview = !hasSelectedPreviewSelection
    ? []
    : hasFreshPreviewResponse
      ? nextProductsPreview
      : lastStableProductsPreviewRef.current;
  const selectedProducts = productsPreview.filter(product => product.newQuantity > 0
    || isMonthlyToAnnualSwitchPreview(product, currentSubscriptions));
  const getProductInterval = (product: OrderProductPreview): PlanInterval => product.billingInterval
    ?? PlanInterval.getIntervalFromPlanCode(product.productCode);
  const hasSelectedProducts = selectedProducts.length > 0;
  const total = productsPreview.reduce((sum, product) => sum + product.total, 0);
  const subTotal = productsPreview.reduce((sum, product) => sum + product.subTotal, 0);
  const discount = productsPreview.reduce((sum, product) => sum + product.discountAmount, 0);
  const tax = productsPreview.reduce((sum, product) => sum + product.tax, 0);
  const monthlyToAnnualSwitchCredit = selectedProducts
    .filter(product => isMonthlyToAnnualSwitchPreview(product, currentSubscriptions))
    .reduce((sum, product) => sum + product.creditAmount, 0);
  const {
    showCouponLine, couponDiscount, promotionDiscount, showDiscountLine,
  } = splitDiscountRows(
    discount,
    appliedCoupon ?? undefined,
    showCoupon,
  );
  // The CE coupon is a one-time ORDER-LEVEL discount: the backend no longer folds it into the
  // product prices, so it is subtracted here from what is due today. The recurring interval
  // totals stay coupon-free — the coupon is single-use and will not apply on renewal.
  const netTotal = total - monthlyToAnnualSwitchCredit - couponDiscount;
  const showTax = tax > 0;
  const suffixForInterval = (interval: PlanInterval) => intl.formatMessage({
    id: interval === PlanInterval.YEAR
      ? 'page.plan.v2.true.testing.price.year'
      : 'page.plan.v2.true.testing.price.month',
  });
  const formatPeriodPriceForInterval = (amount: number, interval: PlanInterval) => (
    `${formatCurrency(intl, amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}${suffixForInterval(interval)}`
  );
  const summarizeInterval = (interval: PlanInterval) => {
    const items = productsPreview.filter(product => getProductInterval(product) === interval);
    const intervalTotal = items.reduce((sum, product) => sum + product.total, 0);
    const intervalCredit = items
      .filter(product => isMonthlyToAnnualSwitchPreview(product, currentSubscriptions))
      .reduce((sum, product) => sum + product.creditAmount, 0);
    return {
      hasItems: selectedProducts.some(product => getProductInterval(product) === interval),
      net: intervalTotal - intervalCredit,
      list: items.reduce((sum, product) => sum + product.subTotal, 0),
    };
  };
  const annualSummary = summarizeInterval(PlanInterval.YEAR);
  const monthlySummary = summarizeInterval(PlanInterval.MONTH);
  const hasMixedIntervals = annualSummary.hasItems && monthlySummary.hasItems;
  // Per-promotion rows of the Discount tooltip ("What you're saving"): one row per
  // discounted product, worded for the starter package when that campaign priced it.
  const discountDetails = selectedProducts
    .filter(product => product.discountAmount > 0)
    .map(product => {
      const feature = getPreviewProductFeature(product);
      const starterLine = getStarterPackageSummaryLine(
        activePromotion,
        product.appliedPromotion,
        isMonthlyToAnnualSwitchPreview(product, currentSubscriptions)
          ? product.quantity
          : product.newQuantity,
        product.subTotal,
      );
      const productTitle = intl.formatMessage({ id: getTrueBundleTitleId(feature) });
      // Tiered offers are worded by the discounted per-seat rate ("first 3 ... at
      // $1,000/yr"), one-time offers by the package total ("5 ... for $4,000/yr").
      const starterLabel = starterLine && intl.formatMessage(
        {
          id: starterLine.kind === 'tiered'
            ? 'page.plan.v2.order.summary.discount.detail.starter.tiered'
            : 'page.plan.v2.order.summary.discount.detail.starter',
        },
        {
          count: starterLine.packageSeats,
          product: productTitle,
          unit: getSummaryUnitWord(feature, starterLine.packageSeats),
          price: formatCurrency(
            intl,
            starterLine.kind === 'tiered'
              ? starterLine.packageUnitPrice
              : starterLine.packagePrice,
            DEFAULT_CURRENCY_FORMAT_OPTIONS,
          ),
          interval: suffixForInterval(getProductInterval(product)),
        },
      );
      return {
        key: product.productCode,
        amount: product.discountAmount,
        label: starterLabel || intl.formatMessage(
          { id: 'page.plan.v2.order.summary.discount.detail.promotion' },
          { product: productTitle },
        ),
      };
    });
  const addOns = getPaidAddOnLineItems(productsPreview);
  const cloudQuota = getCloudSessionQuota(productsPreview, currentSubscriptions);
  const selectedTrueSeatQuota = getSelectedTrueBundleSeatQuota(productsPreview);
  const totalTrueSeatQuota = getFinalTrueBundleSeatQuota(productsPreview, currentSubscriptions);
  const periodSuffix = intl.formatMessage({
    id: isAnnual
      ? 'page.plan.v2.true.testing.price.year'
      : 'page.plan.v2.true.testing.price.month',
  });
  const formatPeriodPrice = (amount: number) => (
    `${formatCurrency(intl, amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}${periodSuffix}`
  );

  const trackOrderSummaryAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        currency: cartSnapshot.currency,
        value: cartSnapshot.value,
        items: cartSnapshot.items,
        ...trackingCookies,
      },
    );
  };

  const handleProceedToCheckout = () => {
    if (hasSelectedProducts) {
      const navigateToCheckout = async () => {
        navigate(
          orderConfirmation.path,
          replaceQuery(parse(queryString()) as QueryDictionary),
        );
      };
      navigateToCheckout().catch(() => {
      });

      sendTrackingData(
        'payment_checkout_button_clicked',
        accountId,
        undefined,
        {
          field: 'plan_confirmation',
          object: 'button',
          action: 'proceed_to_checkout',
          plan_flow: 'true_bundle',
          currency: cartSnapshot.currency,
          value: cartSnapshot.value,
          items: cartSnapshot.items,
          ...trackingCookies,
        },
      );
    }
  };

  useEffect(() => {
    const serialized = JSON.stringify(cartSnapshot);
    if (cartSnapshot.items.length === 0
      || previewCartRef.current === serialized) {
      return;
    }

    previewCartRef.current = serialized;

    sendTrackingData(
      'preview_cart',
      accountId,
      undefined,
      {
        page: 'expand_plan',
        plan_flow: 'true_bundle',
        currency: cartSnapshot.currency,
        value: cartSnapshot.value,
        items: cartSnapshot.items,
        ...trackingCookies,
      },
    );
  }, [accountId, cartSnapshot]);

  return (
    <Paper className={classes.orderSummaryCard} elevation={0}>
      <Typography className={classes.orderSummaryTitle} mb={2}>
        <FormattedMessage id="page.plan.v2.order.summary.title" />
      </Typography>
      <OrderSummaryLineItems
        selectedProducts={selectedProducts}
        currentSubscriptions={currentSubscriptions}
        groupByInterval={groupByInterval}
        onPromotionTooltipOpen={trackOrderSummaryAction}
      />

      {cloudQuota > 0 && (
        <Box className={classes.productBlock}>
          <Typography className={classes.includedFreeLabel}>
            <FormattedMessage id="page.plan.v2.order.summary.included.free" />
          </Typography>
          <Box className={classes.productRow}>
            <Box minWidth={0}>
              <Typography className={classes.productName}>
                <FormattedMessage id="page.plan.v2.true.testing.cross.browser.title" />
              </Typography>
              <Typography className={classes.productQuantity}>
                <Box component="span" className={classes.productQuantityWithInfo}>
                  {getSummaryQuantityLabel(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, cloudQuota)}
                  <Tooltip
                    arrow
                    placement="top"
                    classes={{
                      tooltip: classes.lightTooltip,
                      arrow: classes.lightTooltipArrow,
                    }}
                    onOpen={() => trackOrderSummaryAction({
                      field: 'order_summary',
                      object: 'included_cloud_sessions_tooltip',
                      action: 'view_tooltip',
                      included_cloud_sessions: cloudQuota,
                      selected_true_seat_count: selectedTrueSeatQuota,
                      total_true_seat_count: totalTrueSeatQuota,
                    })}
                    title={intl.formatMessage(
                      {
                        id: 'page.plan.v2.order.summary.included.cloud.sessions.tooltip',
                      },
                      {
                        seatCount: totalTrueSeatQuota,
                        sessionCount: cloudQuota,
                      },
                    )}
                  >
                    <InfoOutlinedIcon className={classes.productQuantityInfoIcon} />
                  </Tooltip>
                </Box>
              </Typography>
            </Box>
            <Typography className={classes.productFreeAmount}>
              <FormattedMessage id="page.plan.v2.order.summary.free" />
            </Typography>
          </Box>
        </Box>
      )}

      {addOns.map(lineItem => (
        <Box key={lineItem.addOnCode} className={classes.productBlock}>
          <Box className={classes.productRow}>
            <Box minWidth={0}>
              <Typography className={classes.productName}>
                <FormattedMessage id={`addon.${lineItem.addOnCode}.display.name`} />
              </Typography>
              <Typography className={classes.productQuantity}>
                {getSummaryQuantityLabel(
                  getFeatureByAddOn(lineItem.addOnCode.toLowerCase()),
                  lineItem.quantity,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.productAmount}>
                {formatPeriodPrice(Math.max(lineItem.subtotal - lineItem.discountAmount, 0))}
              </Typography>
              {lineItem.discountAmount > 0 && (
                <Typography className={classes.productOriginalAmount}>
                  {formatPeriodPrice(lineItem.subtotal)}
                </Typography>
              )}
              {lineItem.tax > 0 && (
                <Typography className={classes.productTaxAmount}>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.product.tax"
                    values={{
                      amount: formatCurrency(intl, lineItem.tax, DEFAULT_CURRENCY_FORMAT_OPTIONS),
                    }}
                  />
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      ))}

      {hasSelectedProducts && (
        <>
          {!groupByInterval && (
            <Box className={classes.summaryRow} pt={2.5} pb={1}>
              <Typography fontWeight={400} fontSize={14}>
                <FormattedMessage id="page.plan.v2.order.summary.standard.price" />
              </Typography>
              <Typography fontWeight={700} fontSize={14}>
                {formatPeriodPrice(subTotal)}
              </Typography>
            </Box>
          )}
          {groupByInterval && (hasMixedIntervals ? (
            <>
              <Box className={classes.summaryRow} pt={2.5} pb={0}>
                <Typography className={classes.summaryLabel}>
                  <FormattedMessage id="page.plan.v2.order.summary.subtotal.annually" />
                </Typography>
                <Typography className={classes.summaryValue}>
                  {formatPeriodPriceForInterval(annualSummary.list, PlanInterval.YEAR)}
                </Typography>
              </Box>
              <Box className={classes.summaryRow} pt={0} pb={0}>
                <Typography className={classes.summaryLabel}>
                  <FormattedMessage id="page.plan.v2.order.summary.subtotal.monthly" />
                </Typography>
                <Typography className={classes.summaryValue}>
                  {formatPeriodPriceForInterval(monthlySummary.list, PlanInterval.MONTH)}
                </Typography>
              </Box>
            </>
          ) : (
            <Box className={classes.summaryRow} pt={2.5} pb={0}>
              <Typography className={classes.summaryLabel}>
                <FormattedMessage id="page.plan.v2.order.summary.subtotal" />
              </Typography>
              <Typography className={classes.summaryValue}>
                {monthlySummary.hasItems
                  ? formatPeriodPriceForInterval(monthlySummary.list, PlanInterval.MONTH)
                  : formatCurrency(intl, annualSummary.list, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
              </Typography>
            </Box>
          ))}
          {showDiscountLine && (
            <Box
              className={classes.summaryRow}
              pt={1}
              pb={showTax || monthlyToAnnualSwitchCredit > 0 || showCouponLine ? 2 : 4}
            >
              <Box component="span" className={classes.discountLabelGroup}>
                <Typography fontWeight={700} fontSize={14} color="#16865F">
                  <FormattedMessage id="page.plan.v2.order.summary.discount" />
                </Typography>
                {discountDetails.length > 0 && (
                  <Tooltip
                    arrow
                    placement="top"
                    classes={{ tooltip: classes.darkTooltip }}
                    onOpen={() => trackOrderSummaryAction({
                      field: 'order_summary',
                      object: 'discount_breakdown_tooltip',
                      action: 'view_tooltip',
                      discount_amount: promotionDiscount,
                    })}
                    title={(
                      <Box>
                        <Typography className={classes.darkTooltipTitle}>
                          <FormattedMessage id="page.plan.v2.order.summary.discount.tooltip.title" />
                        </Typography>
                        {discountDetails.map(detail => (
                          <Box key={detail.key} className={classes.discountDetailRow}>
                            <Typography component="span" className={classes.discountDetailLabel}>
                              {detail.label}
                            </Typography>
                            <Typography component="span" className={classes.discountDetailAmount}>
                              {`-${formatCurrency(intl, detail.amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  >
                    <InfoOutlinedIcon className={classes.discountInfoIcon} />
                  </Tooltip>
                )}
              </Box>
              <Typography className={classes.discountValue}>
                {promotionDiscount > 0
                  ? `-${formatCurrency(intl, promotionDiscount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}`
                  : formatCurrency(intl, promotionDiscount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
              </Typography>
            </Box>
          )}
          {showCoupon && appliedCoupon && (
            <Box
              className={classes.summaryRow}
              pt={0}
              pb={monthlyToAnnualSwitchCredit > 0 || showTax ? 0 : 2}
            >
              <Typography fontWeight={700} fontSize={14} color="#16865F">
                <FormattedMessage
                  id="page.plan.v2.order.summary.coupon.line"
                  values={{
                    code: appliedCoupon.code,
                    discount: formatCouponDiscount(appliedCoupon, intl),
                  }}
                />
              </Typography>
              <Typography className={classes.discountValue}>
                -
                {formatCurrency(
                  intl,
                  appliedCoupon.appliedAmount ?? 0,
                  DEFAULT_CURRENCY_FORMAT_OPTIONS,
                )}
              </Typography>
            </Box>
          )}
          {monthlyToAnnualSwitchCredit > 0 && (
            <Box className={classes.summaryRow} pt={0} pb={2}>
              <Typography fontWeight={700} fontSize={14} color="#16865F">
                <FormattedMessage id="page.plan.v2.order.summary.monthly.credit" />
              </Typography>
              <Typography className={classes.discountValue}>
                -
                {formatCurrency(intl, monthlyToAnnualSwitchCredit, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
              </Typography>
            </Box>
          )}
          {showTax && (
            <Box className={classes.summaryRow} pt={0} pb={4}>
              <Typography fontWeight={400} fontSize={14}>
                <FormattedMessage id="page.order.confirmation.tax" />
              </Typography>
              <Typography fontWeight={700} fontSize={14}>
                {formatCurrency(intl, tax, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
              </Typography>
            </Box>
          )}
          {monthlyToAnnualSwitchCredit > 0 && (
            <Alert
              severity="info"
              sx={theme => ({
                marginBottom: theme.spacing(2),
                '& .MuiAlert-message': {
                  fontSize: 12,
                },
              })}
            >
              <FormattedMessage id="page.plan.v2.order.summary.monthly.switch.note" />
            </Alert>
          )}

          {!groupByInterval && <Divider sx={{ borderStyle: 'dashed', my: 1 }} />}
        </>
      )}

      {groupByInterval ? (
        <Box mt={0}>
          {previewLoading ? (
            <Box className={classes.previewLoadingBox}>
              <CircularProgress color="inherit" size={24} />
            </Box>
          ) : (
            <>
              {hasSelectedProducts && (
                <Box className={classes.totalRow}>
                  <Typography className={classes.totalLabel}>
                    <FormattedMessage id="page.plan.v2.order.summary.due.today" />
                  </Typography>
                  <Typography className={classes.totalAmount}>
                    {formatCurrency(intl, netTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                  </Typography>
                </Box>
              )}
              {monthlySummary.hasItems && (
                <Typography className={classes.recurringNote}>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.recurring.monthly"
                    values={{
                      amount: formatPeriodPriceForInterval(
                        monthlySummary.net,
                        PlanInterval.MONTH,
                      ),
                    }}
                  />
                </Typography>
              )}
            </>
          )}
        </Box>
      ) : (
        <Box className={classes.totalRow} mt={1.5}>
          <Typography className={classes.totalLabel}>
            <FormattedMessage id="page.plan.v2.order.summary.total" />
          </Typography>
          <Box textAlign="right">
            {previewLoading ? (
              <Box className={classes.totalAmountLoading}>
                <CircularProgress color="inherit" size={24} />
              </Box>
            ) : (
              <Typography className={classes.totalAmount}>
                {formatPeriodPrice(netTotal)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {showCoupon && hasSelectedProducts && !hasPendingOrder && (
        <OrderSummaryCoupon isAnnual={isAnnual} />
      )}

      {hasPendingOrder && (
        <Box
          id="expand-plan-price-summary-alert"
          alignItems="center"
          alignContent="center"
          justifyContent="center"
        >
          <Alert
            id="expand-plan-price-summary-checkout-button-alert"
            severity="warning"
            sx={theme => ({
              marginBottom: 2,
              width: '100%',
              '& .MuiAlert-icon': {
                margin: theme.spacing(1, 2, 1, 0),
              },
            })}
          >
            <FormattedMessage id="page.expand.plan.price.summary.pending.order.alert" />
          </Alert>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        className={classes.checkoutButton}
        onClick={handleProceedToCheckout}
        disabled={!!hasPendingOrder
          || !hasSelectedProducts
          || previewLoading
          || !hasFreshPreviewResponse}
      >
        <Box component="span" className={classes.checkoutButtonContent}>
          {previewLoading && <CircularProgress color="inherit" size={18} />}
          <FormattedMessage id="page.plan.v2.checkout.button" />
        </Box>
      </Button>
      <Typography sx={{ mt: 1.5, fontSize: 11.5, lineHeight: 1.5, color: '#9a98ad' }}>
        <FormattedMessage id="page.plan.v2.order.summary.discount.note" />
      </Typography>
    </Paper>
  );
};
